import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router'
import api from '../lib/api'

function DbDot({ status }) {
  if (!status) return <div className="db-dot" style={{ background: 'var(--border)' }} />
  const cls = { ok: 'ok', warn: 'warn', error: 'err' }[status] ?? 'err'
  return <div className={`db-dot ${cls}`} />
}

function TokenBadge({ status }) {
  if (!status) return <span className="badge badge-neutral">—</span>
  const map = {
    ok:         <span className="badge badge-ok">{status.label}</span>,
    por_vencer: <span className="badge badge-warn">{status.label}</span>,
    vencido:    <span className="badge badge-err">Vencido</span>,
    sin_token:  <span className="badge badge-neutral">Sin token</span>,
  }
  return map[status.status] ?? <span className="badge badge-neutral">—</span>
}

export default function Empresas() {
  const navigate = useNavigate()
  const [tenants,  setTenants]  = useState([])
  const [meta,     setMeta]     = useState({})
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState(null)
  const [search,   setSearch]   = useState('')
  const [page,     setPage]     = useState(1)

  const fetchTenants = useCallback((q = search, p = page) => {
    setLoading(true)
    api.get('/tenants', { params: { search: q, page: p, per_page: 20 } })
      .then(({ data }) => {
        setTenants(data.data ?? [])
        setMeta({ total: data.total, last_page: data.last_page, current_page: data.current_page })
      })
      .catch(err => setError(err.response?.data?.message ?? 'Error al cargar.'))
      .finally(() => setLoading(false))
  }, []) // eslint-disable-line

  useEffect(() => { fetchTenants() }, [fetchTenants])

  const handleSearch = (e) => {
    const val = e.target.value
    setSearch(val)
    setPage(1)
    fetchTenants(val, 1)
  }

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb"><strong>Empresas</strong> — Todos los tenants</div>
        <div className="topbar-actions">
          <input className="search-bar" placeholder="🔍  Nombre, NIT, subdominio..." value={search} onChange={handleSearch} />
          <button className="btn btn-primary" onClick={() => navigate('/wizard')}>+ Nuevo cliente</button>
        </div>
      </div>

      <div className="content">
        {error && <div className="error-banner">⚠ {error}</div>}

        <div className="table-wrap">
          <div className="table-toolbar">
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {meta.total ?? '—'} tenants registrados
            </span>
            <div style={{ marginLeft: 'auto' }}>
              <button className="btn btn-ghost btn-sm">↓ CSV</button>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Empresa</th>
                <th>NIT</th>
                <th>Subdominio</th>
                <th>BD</th>
                <th>Host</th>
                <th>Ambiente</th>
                <th>Estado</th>
                <th>Token MH</th>
                <th>DTEs mes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '24px', color: 'var(--muted)' }}>Cargando…</td></tr>
              )}
              {!loading && tenants.length === 0 && (
                <tr><td colSpan={10} className="empty">Sin resultados</td></tr>
              )}
              {!loading && tenants.map(t => (
                <tr key={t.id} style={{ cursor: 'pointer' }}>
                  <td onClick={() => navigate(`/empresas/${t.id}`)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div className="t-logo" style={{ background: 'linear-gradient(135deg,#3b4fd8,#6d28d9)', width: '28px', height: '28px', fontSize: '10px' }}>
                        {(t.company_name ?? '').slice(0,2).toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 600 }}>{t.company_name}</span>
                    </div>
                  </td>
                  <td onClick={() => navigate(`/empresas/${t.id}`)}><span className="mono">{t.nit}</span></td>
                  <td onClick={() => navigate(`/empresas/${t.id}`)}><span className="mono">{t.subdomain}</span></td>
                  <td onClick={() => navigate(`/empresas/${t.id}`)}><span className="mono">{t.db_database}</span></td>
                  <td onClick={() => navigate(`/empresas/${t.id}`)}><span className="mono" style={{ color: 'var(--muted)' }}>{t.db_host}:{t.db_port ?? 3306}</span></td>
                  <td onClick={() => navigate(`/empresas/${t.id}`)}>
                    {t.ambiente === '01'
                      ? <span className="badge badge-prod">Producción</span>
                      : <span className="badge badge-test">Pruebas</span>}
                  </td>
                  <td onClick={() => navigate(`/empresas/${t.id}`)}>
                    {t.active
                      ? <span className="badge badge-ok">Activo</span>
                      : <span className="badge badge-neutral">Inactivo</span>}
                  </td>
                  <td onClick={() => navigate(`/empresas/${t.id}`)}>
                    <TokenBadge status={t.token_status} />
                  </td>
                  <td style={{ fontWeight: 700 }}>{(t.dtes_mes ?? 0).toLocaleString()}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/empresas/${t.id}`)}>Ver →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Paginación */}
          {meta.last_page > 1 && (
            <div className="pagination">
              <span>Página {meta.current_page} de {meta.last_page} · {meta.total} tenants</span>
              <div className="page-btns">
                <div className={`page-btn ${page <= 1 ? 'disabled' : ''}`} onClick={() => { if (page > 1) { setPage(page-1); fetchTenants(search, page-1) }}}>←</div>
                {Array.from({ length: Math.min(meta.last_page, 5) }, (_, i) => i + 1).map(p => (
                  <div key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => { setPage(p); fetchTenants(search, p) }}>{p}</div>
                ))}
                <div className="page-btn" onClick={() => { if (page < meta.last_page) { setPage(page+1); fetchTenants(search, page+1) }}}>→</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
